/**
 * Axios Response Interceptor
 *
 * Handles:
 * - Backend response unwrapping (success/error)
 * - Error normalization
 * - 401/403 handling
 * - Network error detection
 * - Global error notifications (optional)
 * - Request/Response logging (dev only)
 */

import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios"
import axios from "axios"
import { toastErrorMessage } from "@/shared/toast-error"

import { getTokenProvider } from "./request"

import type {
  ApiErrorDto,
  BackendResponse,
  NormalizedError,
} from "../types"
import { HttpStatus } from "../types"

// Enable/disable API logging
const API_LOGGING_ENABLED = import.meta.env.DEV

/**
 * Get request duration from config
 */
function getRequestDuration(config?: InternalAxiosRequestConfig): number {
  const startTime = (config as { _startTime?: number } | undefined)?._startTime
  return startTime ? Date.now() - startTime : 0
}

/**
 * Log API response (dev only)
 */
function logResponse(response: AxiosResponse, unwrappedData: unknown): void {
  if (!API_LOGGING_ENABLED) return

  const method = response.config.method?.toUpperCase() ?? "GET"
  const url = `${response.config.baseURL ?? ""}${response.config.url ?? ""}`
  const duration = getRequestDuration(response.config)
  const status = response.status

  const statusColor = status >= 200 && status < 300 ? "#49cc90" : "#fca130"

  console.groupCollapsed(
    `%c← ${method} %c${url} %c${status} %c${duration}ms`,
    "color: #49cc90; font-weight: bold",
    "color: #999",
    `color: ${statusColor}; font-weight: bold`,
    "color: #888"
  )
  console.log("%cStatus:", "font-weight: bold", status, response.statusText)
  console.log("%cDuration:", "font-weight: bold", `${duration}ms`)
  console.log("%cRaw Response:", "font-weight: bold", response.data)
  console.log("%cUnwrapped Data:", "font-weight: bold", unwrappedData)
  console.groupEnd()
}

/**
 * Log API error (dev only)
 */
function logError(error: AxiosError, normalizedError: NormalizedError): void {
  if (!API_LOGGING_ENABLED) return

  const method = error.config?.method?.toUpperCase() ?? "GET"
  const url = `${error.config?.baseURL ?? ""}${error.config?.url ?? ""}`
  const duration = getRequestDuration(error.config)
  const status = error.response?.status ?? 0

  console.groupCollapsed(
    `%c✖ ${method} %c${url} %c${status || "NETWORK"} %c${duration}ms`,
    "color: #f93e3e; font-weight: bold",
    "color: #999",
    "color: #f93e3e; font-weight: bold",
    "color: #888"
  )
  console.log("%cStatus:", "font-weight: bold", status || "No response")
  console.log("%cDuration:", "font-weight: bold", `${duration}ms`)
  console.log("%cRaw Response:", "font-weight: bold", error.response?.data)
  console.log("%cNormalized Error:", "font-weight: bold", normalizedError)
  console.groupEnd()
}

/**
 * Default error messages by status code
 */
const DEFAULT_ERROR_MESSAGES: Record<number, string> = {
  [HttpStatus.BAD_REQUEST]: "Geçersiz istek. Lütfen girdiğiniz bilgileri kontrol edin.",
  [HttpStatus.UNAUTHORIZED]: "Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.",
  [HttpStatus.FORBIDDEN]: "Bu işlemi gerçekleştirmek için yetkiniz bulunmuyor.",
  [HttpStatus.NOT_FOUND]: "İstenen kaynak bulunamadı.",
  [HttpStatus.CONFLICT]: "Bu işlem mevcut verilerle çakışıyor.",
  [HttpStatus.UNPROCESSABLE_ENTITY]: "Girilen veriler işlenemedi.",
  [HttpStatus.INTERNAL_SERVER_ERROR]: "Sunucu hatası. Lütfen daha sonra tekrar deneyin.",
  [HttpStatus.BAD_GATEWAY]: "Sunucu bağlantısı sağlanamadı.",
  [HttpStatus.SERVICE_UNAVAILABLE]: "Servis geçici olarak kullanım dışı.",
  [HttpStatus.GATEWAY_TIMEOUT]: "Sunucu yanıt vermedi. Lütfen tekrar deneyin.",
}

/**
 * Default error messages by error code
 */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  NOT_FOUND: "İstenen kaynak bulunamadı.",
  VALIDATION_ERROR: "Doğrulama hatası. Lütfen girdiğiniz bilgileri kontrol edin.",
  UNAUTHORIZED: "Bu işlem için yetkiniz bulunmuyor.",
  FORBIDDEN: "Bu işleme erişim izniniz yok.",
  CONFLICT: "Bu işlem mevcut verilerle çakışıyor.",
}

/**
 * Extract field errors from API error details
 */
function extractFieldErrors(
  details: Record<string, string[]> | Record<string, unknown> | null
): Record<string, string[]> {
  if (!details) return {}

  const fieldErrors: Record<string, string[]> = {}

  for (const [key, value] of Object.entries(details)) {
    if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
      fieldErrors[key] = value
    }
  }

  return fieldErrors
}

/**
 * Create NormalizedError from API error response
 */
function createNormalizedError(
  status: number,
  apiError: ApiErrorDto | null,
  originalError: AxiosError | null = null
): NormalizedError {
  const code = apiError?.code ?? null
  const message =
    apiError?.message ??
    (code ? ERROR_CODE_MESSAGES[code] : null) ??
    DEFAULT_ERROR_MESSAGES[status] ??
    "Beklenmeyen bir hata oluştu"

  return {
    status,
    code,
    message,
    fieldErrors: extractFieldErrors(apiError?.details ?? null),
    isNetworkError: false,
    isTimeout: false,
    isCanceled: false,
    originalError,
  }
}

/**
 * Normalize any error to NormalizedError format
 */
export function normalizeError(error: AxiosError): NormalizedError {
  const isNetworkError = !error.response && !axios.isCancel(error)
  const isTimeout = error.code === "ECONNABORTED" || error.code === "ETIMEDOUT"
  const isCanceled = axios.isCancel(error)

  // Network error (no response)
  if (isNetworkError) {
    return {
      status: 0,
      code: error.code ?? "NETWORK_ERROR",
      message: isTimeout
        ? "Sunucu yanıt vermedi. Lütfen internet bağlantınızı kontrol edin."
        : "Sunucuya bağlanılamadı. Lütfen internet bağlantınızı kontrol edin.",
      fieldErrors: {},
      isNetworkError: true,
      isTimeout,
      isCanceled: false,
      originalError: error,
    }
  }

  // Canceled request
  if (isCanceled) {
    return {
      status: 0,
      code: "CANCELED",
      message: "İstek kullanıcı tarafından iptal edildi.",
      fieldErrors: {},
      isNetworkError: false,
      isTimeout: false,
      isCanceled: true,
      originalError: error,
    }
  }

  // Response error - check if it follows BackendResponse format
  const response = error.response!
  const status = response.status
  const rawData = response.data as Record<string, unknown> | undefined

  // Backend may use either "success" or "isSuccess" as the key
  if (rawData && typeof rawData === "object") {
    const isSuccess = rawData["success"] ?? rawData["isSuccess"]
    const errorPayload = rawData["error"] as ApiErrorDto | null | undefined
    if (isSuccess === false && errorPayload) {
      return createNormalizedError(status, errorPayload, error)
    }

    // ASP.NET ProblemDetails / ModelState validation hatası:
    // { title, status, errors: { field: ["msg", ...] } }
    const problemErrors = rawData["errors"]
    if (problemErrors && typeof problemErrors === "object") {
      const fieldErrors = extractFieldErrors(problemErrors as Record<string, string[]>)
      const messages = Object.values(fieldErrors).flat()
      const title = typeof rawData["title"] === "string" ? (rawData["title"] as string) : null
      const message =
        messages.length > 0
          ? messages.join(" ")
          : title ?? DEFAULT_ERROR_MESSAGES[status] ?? "Beklenmeyen bir hata oluştu"
      return {
        status,
        code: "VALIDATION_ERROR",
        message,
        fieldErrors,
        isNetworkError: false,
        isTimeout: false,
        isCanceled: false,
        originalError: error,
      }
    }
  }

  // Fallback for non-standard error responses
  return createNormalizedError(status, null, error)
}

/**
 * Handle authentication errors (401)
 */
function handleUnauthorized(): void {
  const tokenProvider = getTokenProvider()
  tokenProvider.clearTokens()

  window.location.href = "/"
}

/**
 * Handle forbidden errors (403)
 */
function handleForbidden(normalizedError: NormalizedError): void {
  toastErrorMessage(`Yetkisiz İşlem: ${normalizedError.message}`)
}

/**
 * Handle server errors (5xx)
 */
function handleServerError(normalizedError: NormalizedError): void {
  toastErrorMessage(`Sunucu Hatası: ${normalizedError.message}`)

  // Log to console in development
  if (import.meta.env.DEV && normalizedError.code) {
    console.error(`[Server Error] Code: ${normalizedError.code}`)
  }
}

/**
 * Response success interceptor
 * Unwraps BackendResponse and extracts data
 */
export function responseInterceptor(response: AxiosResponse): AxiosResponse {
  const data = response.data as BackendResponse<unknown> | undefined

  // Check if response follows BackendResponse format
  // Backend may use either "success" or "isSuccess" as the key
  const rawData = data as Record<string, unknown> | undefined
  const isBackendResponse =
    rawData &&
    typeof rawData === "object" &&
    ("success" in rawData || "isSuccess" in rawData)

  if (isBackendResponse) {
    const isSuccess = rawData["success"] ?? rawData["isSuccess"]
    const errorPayload = rawData["error"] as ApiErrorDto | null | undefined

    if (isSuccess) {
      // Unwrap successful response - replace response.data with actual data
      response.data = (rawData["data"] ?? rawData["value"]) ?? null
    } else {
      // Backend returned success: false - normalize and throw
      const normalizedError = createNormalizedError(
        response.status,
        errorPayload ?? null,
        null
      )
      const axiosError = new axios.AxiosError(
        normalizedError.message,
        "ERR_BACKEND_ERROR",
        response.config,
        response.request,
        response
      )
      // Attach normalized error so responseErrorInterceptor can re-use it
      ;(axiosError as AxiosError & { normalizedError?: NormalizedError }).normalizedError =
        normalizedError
      throw axiosError
    }
  }

  return response
}

/**
 * Response error interceptor
 */
export function responseErrorInterceptor(error: AxiosError): Promise<never> {
  // If normalizedError was already attached by responseInterceptor (isSuccess: false case), use it directly
  const preNormalized = (error as AxiosError & { normalizedError?: NormalizedError }).normalizedError
  const normalizedError = preNormalized ?? normalizeError(error)

  // Check if global error handling should be skipped
  const skipGlobalHandler = (
    error.config as { skipGlobalErrorHandler?: boolean } | undefined
  )?.skipGlobalErrorHandler

  if (!skipGlobalHandler) {
    // Handle specific status codes
    switch (normalizedError.status) {
      case HttpStatus.UNAUTHORIZED:
        handleUnauthorized()
        break
      case HttpStatus.FORBIDDEN:
        handleForbidden(normalizedError)
        break
      case HttpStatus.INTERNAL_SERVER_ERROR:
      case HttpStatus.BAD_GATEWAY:
      case HttpStatus.SERVICE_UNAVAILABLE:
      case HttpStatus.GATEWAY_TIMEOUT:
        handleServerError(normalizedError)
        break
      default:
        // Network errors
        if (normalizedError.isNetworkError && !normalizedError.isCanceled) {
          toastErrorMessage(`Bağlantı Hatası: ${normalizedError.message}`)
        } else if (
          normalizedError.status >= 400 &&
          normalizedError.status < 500 &&
          !normalizedError.isCanceled
        ) {
          toastErrorMessage(normalizedError.message)
        }
    }
  }

  // Attach normalized error to the rejected promise
  return Promise.reject(normalizedError)
}
